// setupLook — main.js runs it once at boot; the dashboard's mini forest
// (vegforest.js, GET /api/vegforest.js) runs the very same code.
import * as THREE from 'three';
import { HEMI } from './scenelight.js';
import { VEG_LUMA, vegLookOk } from './vegpaint.js';

// The frame's ONE display transform (renderconfig.json `look`; APP_SURFACE.md
// "Light (texture rev 3)"). Every program that ends in three's
// <tonemapping_fragment> — terrain, panorama, buildings, trees, grass, water,
// runtime-lit objects — goes through it; scene values are linear radiance on
// the building light model's scale. shadow_tint > 0 runs the curve through
// CustomToneMapping: linear luminance below shadow_lum leans (luminance-
// preserving) toward the hemisphere sky colour first, so the pre-lit sprites
// get the blue shadows the terrain bake gives its sky-only texels. Must run
// before the first program compiles (the chunk text is read at compile).
// curve key -> [three constant, GLSL function]. Static members only: a
// computed THREE[name] keeps the whole namespace in the bundle.
const _CURVES = {
  agx: [THREE.AgXToneMapping, 'AgXToneMapping'],
  aces: [THREE.ACESFilmicToneMapping, 'ACESFilmicToneMapping'],
  neutral: [THREE.NeutralToneMapping, 'NeutralToneMapping'],
};
const _CUSTOM_STUB = 'vec3 CustomToneMapping( vec3 color ) { return color; }';

export function setupLook(r, look) {
  // the vegetation sprites' colour hook (grass.js vegLook): their canvases
  // are display-ready colours, scaled + desaturated to sit on the scene's
  // radiance scale before the curve
  // (vegpaint.js "the vegetation LOOK": the formula, its luma weights and
  // the JS twin the dashboard preview runs)
  const v = look.veg;
  if (!(v.gain > 0 && v.sat >= 0)) throw new Error('renderconfig.look.veg needs gain > 0, sat >= 0');
  const vs = look.veg_sprite;
  if (!(vs && vegLookOk('gain', vs.gain) && vegLookOk('sat', vs.sat))) {
    throw new Error('renderconfig.look.veg_sprite needs gain in (0, 4], sat in [0, 1.5]');
  }
  const luma = VEG_LUMA.map((x) => x.toFixed(4)).join(', ');
  const vg = v.gain.toFixed(4), vsat = v.sat.toFixed(4);
  THREE.ShaderChunk.veg_look_fragment = `{
    float vl = dot( gl_FragColor.rgb, vec3( ${luma} ) );
    gl_FragColor.rgb = mix( vec3( vl ), gl_FragColor.rgb, ${vsat} ) * ${vg};
  }`;
  // the materials that may carry an OWNER PNG (grass.js vegOwnLook): per
  // fragment the painted look, or uVegLook (the world's sprite_look) where
  // the atlas column holds the owner's picture (vVegOwn 1). mix(a, b, 0) is
  // a exactly: a painted column draws as through veg_look_fragment.
  THREE.ShaderChunk.veg_own_look_fragment = `{
    vec2 lk = mix( vec2( ${vg}, ${vsat} ), uVegLook, vVegOwn );
    float vl = dot( gl_FragColor.rgb, vec3( ${luma} ) );
    gl_FragColor.rgb = mix( vec3( vl ), gl_FragColor.rgb, lk.y ) * lk.x;
  }`;
  if (look.tone === 'none') { r.toneMapping = THREE.NoToneMapping; return; }
  const curve = _CURVES[look.tone];
  if (!curve) throw new Error(`renderconfig.look.tone: unknown curve ${look.tone}`);
  const [mapping, c] = curve;
  r.toneMappingExposure = look.exposure;
  if (!(look.shadow_tint > 0)) { r.toneMapping = mapping; return; }
  const sky = new THREE.Color(HEMI.sky);          // linear working space
  const l = 0.2126 * sky.r + 0.7152 * sky.g + 0.0722 * sky.b;
  const tint = [sky.r / l, sky.g / l, sky.b / l].map((v) => v.toFixed(5)).join(', ');
  const pars = THREE.ShaderChunk.tonemapping_pars_fragment;
  if (!pars.includes(_CUSTOM_STUB)) throw new Error('three tonemapping chunk changed: no CustomToneMapping stub');
  THREE.ShaderChunk.tonemapping_pars_fragment = pars.replace(_CUSTOM_STUB, `vec3 CustomToneMapping( vec3 color ) {
    float l = dot( color, vec3( 0.2126, 0.7152, 0.0722 ) );
    color = mix( color, l * vec3( ${tint} ),
      ${look.shadow_tint.toFixed(4)} * ( 1.0 - smoothstep( 0.0, ${look.shadow_lum.toFixed(4)}, l ) ) );
    return ${c}( color );
  }`);
  r.toneMapping = THREE.CustomToneMapping;
}

// THE FLIGHT'S LOWER RESOLUTION (flight arc_res; owner 2026-10-31 "should be
// smooth, approach and landing"): the scene draws into a sub-rect (k x the
// drawing buffer) of ONE target the drawing buffer's size, and a copy quad
// upsamples it onto the canvas. The canvas, the pixel ratio and every buffer
// stay as they are, so a change of k allocates nothing (setPixelRatio
// re-allocated the canvas: a 300–370 ms frame on the iGPU, bx9) and k can
// ramp. The target is screen-like (isXRRenderTarget, three r170: the
// renderer's tone curve and sRGB output — the canvas's programs, nothing
// compiles); RGBA8 holds the display bytes and the copy writes them as they
// are; its MSAA is the canvas's. Allocated here and at resize() only
// (initRenderTarget). k = 1: unused, the scene draws to the canvas
const _sz = new THREE.Vector2();
export class LookScale {
  constructor(r) {
    this.r = r;
    this.k = 1;
    const a = r.getContext().getContextAttributes();
    r.getDrawingBufferSize(_sz);
    const rt = this.rt = new THREE.WebGLRenderTarget(_sz.x, _sz.y, { samples: a && a.antialias ? 4 : 0, depthBuffer: true,
      generateMipmaps: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
    rt.texture.colorSpace = THREE.SRGBColorSpace;   // the shaders' output: sRGB, encoded in the shader ...
    rt.texture.internalFormat = 'RGBA8';            // ... stored as bytes (never SRGB8_ALPHA8: no decode on the copy)
    rt.isXRRenderTarget = true;                     // screen-like: the tone curve + the output colour space apply
    rt.scissorTest = true;                          // the clear: the sub-rect only
    this.mat = new THREE.ShaderMaterial({
      uniforms: { tMap: { value: rt.texture }, uK: { value: new THREE.Vector2(1, 1) }, uMax: { value: new THREE.Vector2(1, 1) } },
      vertexShader: 'varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
      fragmentShader: 'uniform sampler2D tMap; uniform vec2 uK, uMax; varying vec2 vUv;\n'
        + 'void main() { gl_FragColor = texture2D(tMap, min(vUv * uK, uMax)); }',
      depthTest: false, depthWrite: false, toneMapped: false,
    });
    const q = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.mat);
    q.frustumCulled = false;
    this.scene = new THREE.Scene();
    this.scene.add(q);
    this.cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    r.initRenderTarget(rt);
    r.compile(this.scene, this.cam);
    this.setK(1);
  }
  // the window resized (the drawing buffer's new size): the one other allocation
  resize() {
    this.r.getDrawingBufferSize(_sz);
    if (_sz.x !== this.rt.width || _sz.y !== this.rt.height) { this.rt.setSize(_sz.x, _sz.y); this.r.initRenderTarget(this.rt); }
    this.setK(this.k);
  }
  // the scale (0.25..1): the sub-rect and the copy's reach, nothing allocated
  setK(k) {
    this.k = Math.min(1, Math.max(0.25, k));
    const W = this.rt.width, H = this.rt.height, w = Math.max(1, Math.round(W * this.k)), h = Math.max(1, Math.round(H * this.k));
    this.rt.viewport.set(0, 0, w, h);
    this.rt.scissor.set(0, 0, w, h);
    this.mat.uniforms.uK.value.set(w / W, h / H);
    this.mat.uniforms.uMax.value.set((w - 0.5) / W, (h - 0.5) / H);
  }
  // this frame draws through the target: k < 1 and the target the drawing buffer's size (else the canvas, as ever)
  on() {
    if (!(this.k < 1) || this.r.xr.isPresenting) return false;
    this.r.getDrawingBufferSize(_sz);
    return _sz.x === this.rt.width && _sz.y === this.rt.height;
  }
  begin() { this.r.setRenderTarget(this.rt); }
  // the copy onto the canvas; the frame's counters stay the scene's (famTris, visNote read them).
  // three's state cache is reset first: in a flight it can hold a texture
  // binding the context no longer has, and the copy then sampled black (bx10
  // probe: the second arc black, cured by one reset); a few GL calls a frame
  present() {
    const r = this.r, i = r.info, c = i.render, keep = [c.calls, c.triangles, c.points, c.lines], ar = i.autoReset;
    r.resetState();
    r.setRenderTarget(null);
    i.autoReset = false;
    r.render(this.scene, this.cam);
    i.autoReset = ar;
    [c.calls, c.triangles, c.points, c.lines] = keep;
  }
}
